import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import DescribedField from '../DescribedField';

/**
 * LocationsBlockEditor — Editor do bloco Locais de atendimento
 *
 * A página NÃO guarda mais cópia de local. Ela exibe automaticamente todos os locais ativos do
 * cadastro, com nome, endereço e telefone lidos ao vivo do catálogo no render — pela mesma razão dos
 * blocos de Serviços e Equipe (docs/ANALISE-REFERENCIAS-DINAMICAS-NAS-PAGINAS.md §7).
 *
 * O que sobra é a única decisão que é da PÁGINA: mostrar o mapa ou não. Ela passou a valer para o
 * bloco inteiro, em vez de local a local — não existe "mostrar mapa" no cadastro do local, então
 * esse campo não duplica nada.
 *
 * Props:
 *   - value: { showMap?: boolean }
 *   - onChange: (patch) => void
 *   - context: { availablePlaces }
 */
const LocationsBlockEditor = ({ value = {}, onChange, context = {} }) => {
  const { availablePlaces = [] } = context;
  const ativos = availablePlaces.filter((p) => p?.isActive !== false).length;

  // Páginas publicadas antes da migração trazem `items` com o showMap por local. Enquanto a limpeza
  // do banco não roda, a intenção do dono é lida dali — mesma regra que o renderizador aplica, para
  // o editor não mostrar uma coisa e o site outra.
  const showMap = typeof value.showMap === 'boolean'
    ? value.showMap
    : (Array.isArray(value.items) && value.items.length > 0
       && !value.items.some((it) => !it || it.showMap === false));

  const handleShowMap = (next) => {
    // Descarta `items`: mantê-la aqui a devolveria ao banco no próximo salvar, desfazendo em
    // silêncio o que a migração limpou.
    const { items, ...rest } = value; // eslint-disable-line no-unused-vars
    onChange({ ...rest, showMap: next });
  };

  return (
    <DescribedField
      label="Locais de atendimento"
      description="Todos os locais do seu cadastro aparecem nesta página automaticamente, com o nome, o endereço e o telefone atuais. Para mudar o que aparece, edite o cadastro de Locais."
      helperLink={{ entity: 'place' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {ativos === 0 ? (
          <Alert severity="info">
            Nenhum local cadastrado. Cadastre seus locais para que apareçam nesta página.
          </Alert>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {ativos} local{ativos !== 1 ? 'is' : ''} será{ativos !== 1 ? 'ão' : ''} exibido
            {ativos !== 1 ? 's' : ''} nesta página.
          </Typography>
        )}

        <FormControlLabel
          control={<Checkbox checked={showMap} onChange={(e) => handleShowMap(e.target.checked)} />}
          label="Mostrar mapa"
        />
      </Box>
    </DescribedField>
  );
};

LocationsBlockEditor.propTypes = {
  value: PropTypes.shape({
    showMap: PropTypes.bool,
    // `items` só existe em páginas publicadas antes da migração; é lido para herdar a intenção do
    // dono e nunca é gravado de volta.
    items: PropTypes.array,
  }),
  onChange: PropTypes.func.isRequired,
  context: PropTypes.object,
};

export default LocationsBlockEditor;
